import axios from 'axios';
import { env } from './env';
import { isReloginRequired } from './errors';

export const api = axios.create({
  baseURL: env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Important: send cookies with requests
  withCredentials: true,
  // Serialize arrays as statuses[]=value1&statuses[]=value2
  paramsSerializer: (params) => {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return; // Skip undefined/null values
      }

      if (Array.isArray(value)) {
        // Serialize arrays as key[]=value1&key[]=value2
        value.forEach((item) => {
          if (item !== undefined && item !== null) {
            searchParams.append(`${key}[]`, String(item));
          }
        });
      } else {
        searchParams.append(key, String(value));
      }
    });

    return searchParams.toString();
  },
});

// Custom event for auth state changes
export const AUTH_EVENTS = {
  UNAUTHORIZED: 'auth:unauthorized',
  SESSION_EXPIRED: 'auth:session-expired',
} as const;

/**
 * Dispatch auth event for components to handle
 */
function dispatchAuthEvent(eventType: string) {
  window.dispatchEvent(new CustomEvent(eventType));
}

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;
// Set once the server reports that this tab's impersonation is over; the page
// is reloading, and until it has, nothing may be refreshed or retried — a later
// refresh would be an ordinary one and replay the request as the admin.
let impersonationEnded = false;

// Cookies are shared by every tab, but the flag above is not: a second tab still
// showing the impersonation banner would refresh into the admin's session and
// replay its request. The tab that learns the impersonation ended tells the
// others, and they reload too.
const IMPERSONATION_ENDED_MESSAGE = 'impersonation-ended';

/**
 * Tell every other tab that this browser's identity changed (an impersonation
 * started, ended or was exited): they hold the previous identity's view and
 * would act on it with the new cookies, so they reload.
 */
export function announceIdentityChange() {
  impersonationChannel?.postMessage(IMPERSONATION_ENDED_MESSAGE);
}
let impersonationChannel: BroadcastChannel | null = null;
try {
  // Browser only: Node has a BroadcastChannel too, and one left open would keep
  // a test process alive.
  if (typeof window !== 'undefined') impersonationChannel = new BroadcastChannel('hygge-auth');
  if (impersonationChannel) impersonationChannel.onmessage = (event: MessageEvent) => {
    if (event.data === IMPERSONATION_ENDED_MESSAGE && !impersonationEnded) {
      impersonationEnded = true;
      window.location.assign('/');
    }
  };
} catch {
  // No BroadcastChannel (an old browser): each tab still protects itself.
}

// Request interceptor to handle FormData
api.interceptors.request.use((config) => {
  // If data is FormData, remove Content-Type header to let browser set it with boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only skip endpoints that must not trigger a silent refresh:
    // `/auth/refresh` (refreshing on its own 401 would loop) and `/auth/logout`.
    // Everything else under `/auth/` — notably `/auth/me` and `/auth/check`,
    // which the app calls on boot/reload — must go through refresh-and-retry so
    // a still-valid refresh_token cookie keeps the rolling session alive instead
    // of bouncing the user to a full Google re-login.
    const isAuthEndpoint =
      originalRequest.url?.includes('/auth/refresh') ||
      originalRequest.url?.includes('/auth/logout');

    // Handle 401 errors - token expired
    // A 401 marked `reloginRequired` is the server ending the session on
    // purpose (a refused Exit from an impersonation): it has cleared the refresh
    // cookie, so a refresh could only fail and fire SESSION_EXPIRED before the
    // caller has told the user why. The caller signs out itself. [HW-942]
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint &&
      !isReloginRequired(error)
    ) {
      if (impersonationEnded) return Promise.reject(error);
      originalRequest._retry = true;

      // If already refreshing, wait for it
      if (isRefreshing && refreshPromise) {
        try {
          await refreshPromise;
          if (impersonationEnded) return Promise.reject(error);
          return api(originalRequest);
        } catch {
          return Promise.reject(error);
        }
      }

      isRefreshing = true;

      refreshPromise = (async () => {
        let endedNow = false;
        try {
          // Try to refresh the token (cookies are sent automatically)
          const response = await api.post<{ impersonationEnded?: boolean }>('/auth/refresh');
          endedNow = !!response.data?.impersonationEnded;
        } catch {
          // Refresh failed - dispatch event for auth handling
          dispatchAuthEvent(AUTH_EVENTS.SESSION_EXPIRED);
          throw error;
        } finally {
          isRefreshing = false;
          refreshPromise = null;
        }
        if (endedNow) {
          impersonationEnded = true;
          impersonationChannel?.postMessage(IMPERSONATION_ENDED_MESSAGE);
          // The server ended the impersonation and gave this tab no access
          // token; after the reload the refresh cookie signs the admin back in.
          // Nothing queued as the impersonated person may be replayed under the
          // admin's identity, and every in-memory store still holds the other
          // person's view — reload instead of retrying.
          window.location.assign('/');
          throw error;
        }
      })();

      try {
        await refreshPromise;
        // A message from another tab may have ended the impersonation while
        // this refresh was in flight; then nothing is replayed.
        if (impersonationEnded) return Promise.reject(error);
        // Retry the original request
        return api(originalRequest);
      } catch {
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
